import React, { useState } from "react"
import styled, { css } from "styled-components"
import { graphql, useStaticQuery } from "gatsby"
import { Text } from "@contentful/rich-text-types/dist/types/types"
import { AppFunctionComponent } from "../../../../types"
import Chart from "./chart"
import Tooltip, { TooltipProps } from "./tooltip"
import {
  ContentfulSectionElement,
  SectionQueryResultTranslated,
} from "../../../../types"
import Description from "./description"
import SliderNavigation from "../../../common/slider/slider-navigation"
import { useLocalizationContext } from "../../../../context/localization.context"

const NavigationContainer = styled(SliderNavigation)`
  justify-content: center;

  @media (min-width: ${({ theme }) => theme.breakpoint.tablet}px) {
    display: none;
  }
`

const ChartContainer = styled(Chart)`
  margin: 0 15px;

  @media (min-width: ${({ theme }) => theme.breakpoint.tablet}px) {
    margin: 0;
  }
`
const TooltipContainer = styled(Tooltip)<{ active: boolean }>`
  @keyframes fadein {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }

  opacity: 0;
  display: none;
  margin: 30px 20px;

  @media (min-width: ${({ theme }) => theme.breakpoint.tablet}px) {
    position: absolute;
    width: 100%;
    left: 50%;
    transform: translateX(-50%);
    top: var(--tooltip-top);
    margin: 0;
  }

  ${({ active }) =>
    active &&
    css`
      opacity: 1;
      display: block;
      animation: fadein 0.3s ease-in-out 1;
    `}
`

const Container = styled.div`
  @media (min-width: ${({ theme }) => theme.breakpoint.tablet}px) {
    --tooltip-top: -60px;

    position: relative;
    margin-top: calc(var(--tooltip-top) * -1);
  }
`

const mapToTooltipPropsList = (
  sectionElements: ContentfulSectionElement[]
): TooltipProps[] => {
  return sectionElements.map((sectionElement) => {
    const title = sectionElement.title ? sectionElement.title : ""
    const text = sectionElement.text
      ? (JSON.parse(sectionElement.text.raw).content[0].content[0] as Text)
          .value
      : ""

    return { title, text }
  })
}

const MobileSubscriptionsChart: AppFunctionComponent = () => {
  const {
    content: { nodes },
  }: SectionQueryResultTranslated = useStaticQuery(query)
  const { locale } = useLocalizationContext()
  const translatedContent =
    nodes.find((node) => node.node_locale === locale) || nodes[0]
  const { sectionElement, text } = translatedContent
  const tooltipPropsList = sectionElement
    ? mapToTooltipPropsList(sectionElement)
    : []

  const [activeCircleIndex, setActiveCircleIndex] = useState(0)

  return (
    <Container>
      <ChartContainer
        activeCircleIndex={activeCircleIndex}
        onActiveCircleIndexChange={setActiveCircleIndex}
      />
      {text && <Description text={text} />}
      {tooltipPropsList.map((tooltipProps, index) => {
        return (
          <TooltipContainer
            key={index}
            active={activeCircleIndex === index}
            {...tooltipProps}
          />
        )
      })}
      <NavigationContainer
        title={"Global mobile subscriptions"}
        count={tooltipPropsList.length}
        index={activeCircleIndex}
        onIndexChange={setActiveCircleIndex}
      />
    </Container>
  )
}

export default MobileSubscriptionsChart

const query = graphql`
  query SarCheckerMobileSubscriptionsChartSectionQuery {
    content: allContentfulSection(
      filter: { contentfulid: { eq: "sar-checker/mobile-subscriptions-chart" } }
    ) {
      nodes {
        node_locale
        text {
          raw
          references {
            ...Footnote
          }
        }
        sectionElement {
          title
          text {
            raw
          }
        }
      }
    }
  }
`
